import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Button,
  Card,
  Descriptions,
  Empty,
  Table,
  Tag,
  Upload,
  message,
} from 'antd';
import {
  ArrowRightOutlined,
  FileTextOutlined,
  UploadOutlined,
} from '@ant-design/icons';
import MainLayout from '../../layouts/MainLayout';
import { financeSettingsApi } from '../../utils/api';
import { DetailPageSkeleton } from '../../components/SkeletonLoaders';
import { formatMoneyFa as formatMoney } from '../../utils/formatPriceToman';
import { formatJalaliApiDate } from '../../utils/dateUtils';
import { useDetailReturnRefs } from '../../hooks/useListReturnState';
import { useEntityBreadcrumb } from '../../hooks/useEntityBreadcrumb';
import EntityBreadcrumb from '../../components/common/EntityBreadcrumb';
import { formatItemDiscountLabel } from '../../utils/itemDiscountUtils';
import { formatClientNameWithCompany, resolveClientCompanyName } from '../../utils/clientDisplayUtils';

const statusTag = {
  unpaid: { color: 'red', text: 'پرداخت نشده' },
  paid: { color: 'green', text: 'پرداخت شده' },
  partial: { color: 'orange', text: 'پرداخت ناقص' },
  canceled: { color: 'default', text: 'لغو شده' },
};

const ViewInvoice = () => {
  const navigate = useNavigate();
  const { returnToRef, buildGoBackState } = useDetailReturnRefs();
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [fileList, setFileList] = useState([]);
  const [data, setData] = useState(null);
  const invoiceForCrumb = data?.invoice;
  const { trail, goToCrumb } = useEntityBreadcrumb({
    listPath: '/finance/invoices',
    listLabel: 'فاکتورها',
    entityLabel: invoiceForCrumb?.invoice_number || (id ? `فاکتور #${id}` : 'فاکتور'),
  });

  const goBackToList = () => {
    navigate(returnToRef.current || '/finance/invoices', { state: buildGoBackState() });
  };

  const fetchInvoice = async () => {
    setLoading(true);
    try {
      const res = await financeSettingsApi.getInvoice(id);
      setData(res?.data || null);
    } catch (error) {
      message.error('خطا در دریافت اطلاعات فاکتور');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInvoice();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleUpload = async () => {
    if (!fileList.length) {
      message.warning('فایل را انتخاب کنید');
      return;
    }
    setUploadLoading(true);
    try {
      await financeSettingsApi.storeInvoiceFile(id, fileList[0]);
      message.success('فایل با موفقیت ذخیره شد');
      setFileList([]);
      fetchInvoice();
    } catch (error) {
      message.error('خطا در ذخیره فایل');
    } finally {
      setUploadLoading(false);
    }
  };

  if (loading || !data?.invoice) {
    return (
      <MainLayout>
        <div style={{ fontFamily: 'CustomFont' }}>
          <DetailPageSkeleton />
        </div>
      </MainLayout>
    );
  }

  const invoice = data.invoice;
  const status = statusTag[invoice.status] || { color: 'default', text: invoice.status || '-' };
  const clientCompanyName = resolveClientCompanyName(invoice.client, data.client, invoice);
  const clientLabel =
    formatClientNameWithCompany(invoice.client, data.client, invoice) || '-';

  return (
    <MainLayout>
      <div style={{ fontFamily: 'CustomFont' }}>
        <Card className="shadow-xl border-0" bodyStyle={{ padding: 0 }}>
          <EntityBreadcrumb trail={trail} onNavigate={goToCrumb} />
          <div className="flex items-center justify-between flex-wrap gap-4 p-6 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 rounded-t-xl">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center">
                <FileTextOutlined className="text-blue-600 dark:text-blue-400 text-xl" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100 m-0">جزئیات فاکتور</h1>
                <p className="text-gray-500 dark:text-gray-400 text-sm m-0">{invoice.invoice_number}</p>
              </div>
            </div>
            <Button icon={<ArrowRightOutlined />} onClick={goBackToList}>
              بازگشت به لیست
            </Button>
          </div>

          <div className="p-6 bg-gray-50 dark:bg-gray-900/50 rounded-b-xl">
            <Card className="shadow-sm border border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-800 mb-4">
              <Descriptions bordered column={2} style={{ fontFamily: 'CustomFont' }}>
                <Descriptions.Item label="شماره فاکتور">{invoice.invoice_number}</Descriptions.Item>
                <Descriptions.Item label="وضعیت"><Tag color={status.color}>{status.text}</Tag></Descriptions.Item>
                <Descriptions.Item label="مشتری">{clientLabel}</Descriptions.Item>
                {clientCompanyName ? (
                  <Descriptions.Item label="نام شرکت">{clientCompanyName}</Descriptions.Item>
                ) : null}
                <Descriptions.Item label="تاریخ صدور">{formatJalaliApiDate(invoice.issue_date) || '-'}</Descriptions.Item>
                <Descriptions.Item label="تاریخ سررسید">{formatJalaliApiDate(invoice.due_date) || '-'}</Descriptions.Item>
                <Descriptions.Item label="جمع کل">{formatMoney(invoice.total, invoice.currency?.currency_symbol)}</Descriptions.Item>
                <Descriptions.Item label="مانده">{formatMoney(invoice.due_amount, invoice.currency?.currency_symbol)}</Descriptions.Item>
                <Descriptions.Item label="یادداشت" span={2}>{invoice.note || '-'}</Descriptions.Item>
              </Descriptions>
            </Card>

            <Card title="آیتم‌ها" className="shadow-sm border border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-800 mb-4">
              <Table
                rowKey="id"
                dataSource={invoice.items || []}
                pagination={false}
                locale={{ emptyText: <Empty description="آیتمی وجود ندارد" /> }}
                columns={[
                  { title: 'نام آیتم', dataIndex: 'item_name' },
                  { title: 'تعداد', dataIndex: 'quantity' },
                  { title: 'قیمت واحد', dataIndex: 'unit_price', render: (v) => formatMoney(v) },
                  {
                    title: 'تخفیف آیتم',
                    key: 'item_discount',
                    render: (_, row) => formatItemDiscountLabel(row, formatMoney),
                  },
                  { title: 'مبلغ', dataIndex: 'amount', render: (v) => formatMoney(v) },
                ]}
              />
            </Card>

            <Card title="فایل فاکتور" className="shadow-sm border border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-800">
              <div className="flex flex-wrap gap-2 items-center">
                <Upload
                  beforeUpload={(file) => {
                    setFileList([file]);
                    return false;
                  }}
                  onRemove={() => setFileList([])}
                  fileList={fileList.map((f, i) => ({ uid: String(i), name: f.name, status: 'done' }))}
                  maxCount={1}
                >
                  <Button icon={<UploadOutlined />}>انتخاب فایل</Button>
                </Upload>
                <Button type="primary" onClick={handleUpload} loading={uploadLoading} className="bg-green-600 hover:bg-green-700 border-0">
                  ذخیره فایل
                </Button>
              </div>
            </Card>
          </div>
        </Card>
      </div>
    </MainLayout>
  );
};

export default ViewInvoice;
